import moment from 'moment'
import numeral from 'numeral'

export interface LegacyCellValueHolder {
    value: LegacyCellValue
}

export type LegacyCellValue = string | number | boolean

export interface LegacyHoverValueHolder {
    value: LegacyCellValue | undefined
}

export type LegacyCellValueFn<RowDataType> = (rowData: LegacyRowData<RowDataType>) => LegacyCellValue
export type LegacyHoverValueFn<RowDataType> = (rowData: LegacyRowData<RowDataType>) => LegacyCellValue | undefined

export type LegacyRowData<RowDataType> = RowDataType

export interface LegacyNestedTable<RowDataType> {
    isExpanded: boolean
    table: LegacyTable<RowDataType, undefined>
}

export interface LegacyRowOptions {
    isSelectable: boolean
    /** This will greatly affect performance for large tables */
    getDefaultCellColor?: () => string | undefined
    defaultCellColor?: string
}

export interface LegacyHoverOptions<RowDataType> {
    valueFn: (LegacyCellValueFn<RowDataType> | undefined)[]
    data: LegacyRowData<RowDataType>
}

export interface LegacyRowClickEvent<RowDataType, NestedRowDataType> {
    row: LegacyTableRow<RowDataType, NestedRowDataType>
    cellIndex: number
}

export interface LegacyTableOptions {
    isSortable: boolean
    currentSort: LegacyCurrentSort
    pagination: LegacyTablePagination | false
}

export interface LegacyCurrentSort {
    columnIndex: number
    ascending: boolean
}

export class LegacyTable<RowDataType, NestedRowDataType> {
    public options: { isSortable: boolean; currentSort: LegacyCurrentSort; pagination: LegacyTablePagination | false }
    constructor(
        public columns: LegacyTableColumn<RowDataType>[],
        public rows: LegacyTableRow<RowDataType, NestedRowDataType>[],
        pagination: LegacyTablePagination | false,
        isSortable: boolean,
        currentSort?: LegacyCurrentSort
    ) {
        const defaultCurrentSort = { columnIndex: -1, ascending: true }
        this.options = {
            isSortable,
            currentSort: currentSort || defaultCurrentSort,
            pagination,
        }
    }

    get skip(): number {
        return this.options.pagination ? this.options.pagination.skip : 0
    }

    get limit(): number {
        return this.options.pagination ? this.options.pagination.limit : this.rows.length
    }

    sort(columnIndex: number) {
        if (!this.options.isSortable) {
            return false
        }
        const columns = this.columns
        const currentSort = this.options.currentSort
        const rows = this.rows

        let ascending = false
        if (currentSort.columnIndex === columnIndex) {
            if (!currentSort.ascending) {
                ascending = true
            }
        }

        let sortFunction = this.defaultSort
        const providedSortOption = columns[columnIndex].sortAs
        if (typeof providedSortOption === 'function') {
            sortFunction = providedSortOption
        } else {
            if (providedSortOption === 'date') {
                sortFunction = this.dateSort
            } else if (providedSortOption === 'number') {
                sortFunction = this.numberSort
            }
        }

        rows.sort((rowA: LegacyTableRow<RowDataType, NestedRowDataType>, rowB: LegacyTableRow<RowDataType, NestedRowDataType>) => {
            const objectA = rowA.cellValues[columnIndex]
            const objectB = rowB.cellValues[columnIndex]

            return sortFunction(objectA.value, objectB.value, ascending)
        })

        currentSort.ascending = ascending
        currentSort.columnIndex = columnIndex
        return true
    }

    private numberSort(rowCellA: LegacyCellValue, rowCellB: LegacyCellValue, ascending: boolean): -1 | 0 | 1 {
        try {
            const valueA = numeral(rowCellA).value()
            const valueB = numeral(rowCellB).value()
            const difference = valueA - valueB

            if (difference > 0) {
                return ascending ? 1 : -1
            }

            if (difference < 0) {
                return ascending ? -1 : 1
            }
        } catch (err) {}

        return 0
    }

    private dateSort(rowCellA: LegacyCellValue, rowCellB: LegacyCellValue, ascending: boolean): -1 | 0 | 1 {
        try {
            const valueA = moment(typeof rowCellA !== 'boolean' ? rowCellA : '', 'MM/DD/YYYY h:mm A')
            const valueB = moment(typeof rowCellB !== 'boolean' ? rowCellB : '', 'MM/DD/YYYY h:mm A')
            const difference = valueA.diff(valueB)

            if (difference > 0) {
                return ascending ? 1 : -1
            }

            if (difference < 0) {
                return ascending ? -1 : 1
            }
        } catch (err) {}

        return 0
    }
    private defaultSort(rowCellA: LegacyCellValue, rowCellB: LegacyCellValue, ascending: boolean): -1 | 0 | 1 {
        try {
            if (ascending) {
                if (rowCellA < rowCellB) {
                    return -1
                }

                if (rowCellB < rowCellA) {
                    return 1
                }
            } else {
                if (rowCellA < rowCellB) {
                    return 1
                }

                if (rowCellB < rowCellA) {
                    return -1
                }
            }
        } catch (err) {}

        return 0
    }
}

export class LegacyTableRow<RowDataType, NestedRowDataType> {
    public readonly nestedTable: LegacyTable<NestedRowDataType, undefined>
    public areNestedExpanded: boolean
    public readonly isSelectable: boolean
    public isSelected: boolean
    public cellColorValues: string[] = []
    public cellTextColorValues: string[] = []
    private _data: LegacyRowData<RowDataType>
    public cellValues: LegacyCellValueHolder[] = []
    public displayValues: LegacyCellValueHolder[] = []
    private _hoverOptions?: LegacyHoverOptions<RowDataType>
    public hoverValues: LegacyHoverValueHolder[] = []

    constructor(
        public id: string,
        data: LegacyRowData<RowDataType>,
        private columns: LegacyTableColumn<RowDataType>[],
        public options: LegacyRowOptions,
        nestedData?: LegacyNestedTable<NestedRowDataType>
    ) {
        this.nestedTable = nestedData ? nestedData.table : new LegacyTable<NestedRowDataType, undefined>([], [], false, false)
        this.areNestedExpanded = nestedData ? nestedData.isExpanded : false
        this.isSelectable = options && options.isSelectable
        this.isSelected = false
        this._data = data
        this.data = data
    }

    hasNestedRows() {
        return this.nestedTable.rows.length > 0
    }

    private updateCellValues() {
        const MAX_LENGTH = 75
        this.cellValues = []
        this.displayValues = []
        for (const column of this.columns) {
            const fn = column.cellValueFn
            const possiblyUndefinedCellValue: LegacyCellValue = fn(this.data)
            const cellValue = possiblyUndefinedCellValue === undefined ? '' : possiblyUndefinedCellValue
            let slicedCellValue = cellValue
            if (slicedCellValue) {
                slicedCellValue = `${cellValue}`.length > MAX_LENGTH + 3 ? `${cellValue}`.slice(0, MAX_LENGTH).trim() + '...' : cellValue
            }
            this.cellValues.push({ value: cellValue })
            this.displayValues.push({ value: slicedCellValue })
        }
    }

    private updateHoverValues() {
        this.hoverValues = []
        if (this.hoverOptions && this.hoverOptions.data) {
            for (const fn of this.hoverOptions.valueFn) {
                const value = fn ? fn(this.hoverOptions.data) : undefined
                this.hoverValues.push({ value })
            }
        }
    }

    set data(data: LegacyRowData<RowDataType>) {
        this._data = data
        this.updateCellValues()
    }

    get data() {
        return this._data
    }

    set hoverOptions(hoverOptions: LegacyHoverOptions<RowDataType> | undefined) {
        this._hoverOptions = hoverOptions
        this.updateHoverValues()
    }
    get hoverOptions(): LegacyHoverOptions<RowDataType> | undefined {
        return this._hoverOptions
    }
}

export type LegacyColumnSortOption = 'default' | 'number' | 'date' | ((a: LegacyCellValue, b: LegacyCellValue, ascending: boolean) => -1 | 0 | 1)
export class LegacyTableColumn<RowDataType> {
    constructor(
        public readonly label: string,
        public readonly cellValueFn: LegacyCellValueFn<RowDataType>,
        public sortAs: LegacyColumnSortOption
    ) {}
}

export class LegacyTablePagination {
    rowCount: number
    private _firstRecord = 1
    private _pageSizeText = '25'
    private _pageSizeNumber = 25
    private _skip = 0
    private _limit = 25

    constructor(firstRecord: number, pageSize: number, rowCount: number) {
        // The order is important
        this.rowCount = rowCount
        this.pageSizeText = '' + pageSize
        this.firstRecord = firstRecord
    }

    get firstRecord(): number {
        return this._firstRecord
    }

    set firstRecord(value: number) {
        if (this.rowCount === undefined) {
            throw new Error('Pagination needs Max set before you set a record')
        }

        let newValue = value

        if (value < 1) {
            newValue = 1
        }

        if (value > this.rowCount) {
            newValue = this.rowCount
        }

        this._firstRecord = newValue
        this.updateSkip()
        this.updateLimit()
    }

    get pageSizeText(): string {
        return this._pageSizeText
    }

    set pageSizeText(value: string) {
        this._pageSizeText = value
        this._pageSizeNumber = Number(value)
        this.updateLimit()
    }

    get pageSizeNumber(): number {
        return this._pageSizeNumber
    }

    private updateSkip() {
        this._skip = this.firstRecord - 1
    }

    private updateLimit() {
        this._limit = this.firstRecord + this.pageSizeNumber - 1
    }

    get skip() {
        return this._skip
    }

    get limit() {
        return this._limit
    }
}

export class LegacyTableSetup<RowDataType extends { id: string }, NestedRowDataType extends { id: string } | undefined> {
    table: undefined | LegacyTable<RowDataType, NestedRowDataType>
    selectedRows: LegacyTableRow<RowDataType, NestedRowDataType>[] = []

    constructor(
        public columns: LegacyTableColumn<RowDataType>[],
        public pagination: LegacyTablePagination | false,
        public isSortable: boolean,
        public rowOptions: LegacyRowOptions,
        public selectionType: 'SINGLE' | 'MULTIPLE'
    ) {}

    private addRowToTable(rowDataItem: RowDataType) {
        if (!this.table) {
            this.table = new LegacyTable(this.columns, [], this.pagination, this.isSortable)
        }

        let foundRow = false
        for (let i = this.table.rows.length - 1; i > -1; i--) {
            const row = this.table.rows[i]
            if (row.id === rowDataItem.id) {
                const replacementRow = new LegacyTableRow<RowDataType, NestedRowDataType>(rowDataItem.id, rowDataItem, this.columns, this.rowOptions)
                this.table.rows.splice(i, 1, replacementRow)
                foundRow = true

                if (row.isSelected) {
                    row.isSelected = false
                    replacementRow.isSelected = true
                    for (let j = this.selectedRows.length - 1; j > -1; j--) {
                        if (this.selectedRows[j].id === row.id) {
                            this.selectedRows.splice(j, 1, replacementRow)
                        }
                    }
                }
            }
        }
        if (!foundRow) {
            this.table.rows.push(new LegacyTableRow<RowDataType, NestedRowDataType>(rowDataItem.id, rowDataItem, this.columns, this.rowOptions))
        }
    }

    addRowsToTable(rowDataItems: RowDataType[]) {
        for (const rowDataItem of rowDataItems) {
            this.addRowToTable(rowDataItem)
        }
    }

    onRowClicked(ev: LegacyRowClickEvent<RowDataType, NestedRowDataType>) {
        if (this.selectionType === 'SINGLE') {
            const removedRows = this.selectedRows.splice(0, this.selectedRows.length)
            removedRows.forEach((r) => {
                if (r.id !== ev.row.id) {
                    r.isSelected = false
                }
            })
            if (ev.row.isSelected) {
                this.selectedRows.push(ev.row)
            }
        } else if (this.selectionType === 'MULTIPLE') {
            this.selectedRows.splice(0, this.selectedRows.length)
            if (this.table) {
                this.selectedRows.push(...this.table.rows.filter((r) => r.isSelected))
            }
        }
    }

    clear() {
        this.table = undefined
        this.selectedRows.splice(0, this.selectedRows.length)
    }
}
